import { Component, Prop, h, Event, EventEmitter, Element, Method, State } from '@stencil/core';
import { Color } from '../../interface';
import { Camera, DEFAULT_UPLINK } from '../rtsp-h264-mjpeg/camera';
import { CameraOpts } from '../rtsp-h264-mjpeg/rtsp-h264-mjpeg-interface';
import { SimpleUserOptions as SipUserOptions, SimpleUser as SipUser, SimpleUserDelegate as SipUserDelegate, SimpleUser } from 'sip.js/lib/platform/web';
import { Button, SipOpts } from './intercom-interface';

@Component({
  tag: 'lar-intercom-content',
  shadow: false,
  scoped: true,
  styleUrl: 'intercom.scss',
})
export class LarvaIntercomContent {
  @Element() el!: HTMLElement;

  @Prop() colorInputs?: Color;
  @Prop() disabled?: boolean;
  @Prop() camera?: CameraOpts;
  @Prop() sip?: SipOpts;
  @Prop() buttons: Button[] = [];
   /**
    * Use upstream proxy for cameras
    */
  @Prop() useUplinkProxy: boolean = true;
   /**
    * server for cameras
    */
  @Prop({ mutable: true }) server!: string;

  @Event() output!: EventEmitter;
  @Event() loading!: EventEmitter;
  @Event() response!: EventEmitter;

  @State() sipConnected = false;
  @State() sipConnectionFailure = false;
  @State() cameraLoading = true;
  @State() inCall = false;
  @State() calling = false;
  @State() incomingCall = false;
  @State() errorMessage?: string;

  private cameraInstance?: Camera;
  private sipInstance?: SipUser;
  private dom = false;

  @Method()
   public async open () {
     return this.cameraInstance?.open();
   }

  public componentWillLoad() {
    this.dom = true;
    this.errorMessage = '';
    if (!this.server && !this.useUplinkProxy) { // we dont have to use uplink proxy and we dont have device url defined; generate from current host
      this.server = Camera.getLocalVideoServer();
    } else if (!this.server) { // we do have to use uplink, but no server defined, use default
      this.server = DEFAULT_UPLINK;
    }
   }

  public async disconnectedCallback() {
    this.dom = false;
    // tslint:disable-next-line: no-unbound-method
    this.cameraInstance?.$el.removeEventListener('larvideoloading', this.onVideoElementLoading)
    this.cameraInstance?.close();
    if (this.inCall) {
      await this.sipInstance?.hangup()
        .catch(err => console.error(err));
    }
    await this.sipInstance?.unregister()
      .catch(err => console.error(err))
      .then(() => this.sipInstance.disconnect())
      .catch(err => console.error(err));
  }

  public componentDidLoad() {
    this.onVideoElementLoading = this.onVideoElementLoading.bind(this);
    const el = this.el.shadowRoot || this.el;
    // load camera
    if (this.camera) {
      this.cameraLoading = true;
      this.cameraInstance = new Camera(this.camera, this.server);
      if (this.useUplinkProxy) {
        this.output.emit({ upstream: this.cameraInstance.wsurl });
      } else {
        this.cameraInstance.open()
          .catch(err => console.error(err));
      }
      const content = el.querySelector('.camera');
      content.appendChild(this.cameraInstance.$el);
      // tslint:disable-next-line: no-unbound-method
      this.cameraInstance.$el.addEventListener('larvideoloading', this.onVideoElementLoading)
    }
    // load sip
    if (this.sip) {
      this.loading.emit(true);
      const simpleUserDelegate: SipUserDelegate = {
        onCallReceived: this.onCallReceived.bind(this),
        onCallCreated: this.onCallCreated.bind(this),
        onCallAnswered: this.onCallAnswered.bind(this),
        onCallHangup: this.onCallHangup.bind(this),
        onCallHold: this.onCallHold.bind(this),
      };
      const audioElement = el.querySelector('#remoteAudio') as HTMLAudioElement;
      const simpleUserOptions: SipUserOptions = {
        delegate: simpleUserDelegate,
        media: { remote: { audio: audioElement } },
        aor: this.sip.aor,
        userAgentOptions: {
          logLevel: 'error',
          userAgentString: 'Larva.io SIP Client',
          authorizationPassword: this.sip.authorizationPassword,
          authorizationUsername: this.sip.authorizationUsername,
          viaHost: this.sip.viaHost,
        }
      };
      this.sipInstance = new SipUser(this.sip.server, simpleUserOptions);
      this.sipInstance.connect()
        .then(() => {
          this.sipConnected = true;
          return this.sipInstance.register();
        }).catch(err => {
          // @TODO
          this.sipConnectionFailure = true;
          this.errorMessage = err.message;
        }).finally(() => {
          if (this.dom) {
            this.loading.emit(false);
          }
        });
    } else {
      this.loading.emit(false);
    }
  }

  private onVideoElementLoading(event: CustomEvent) {
    this.cameraLoading = event.detail;
  }

  private onCallReceived() {
    this.calling = false;
    this.inCall = false;
    this.incomingCall = true;
  }

  private onCallCreated() {
    // console.log(`[Call created`);
  }

  private onCallAnswered() {
    this.calling = false;
    this.inCall = true;
    this.incomingCall = false;
  }

  private onCallHangup() {
    this.calling = false;
    this.inCall = false;
    this.incomingCall = false;
  }

  private onCallHold(held: boolean) {
    // console.log(`[Call hold ${held}`);
  }

  private async call() {
    try {
      this.errorMessage = '';
      this.calling = true;
      if (this.incomingCall) {
        this.incomingCall = false;
        await this.sipInstance?.answer();
      } else {
        await this.sipInstance?.call(this.sip.target, undefined, {
          // An example of how to get access to a SIP response message for custom handling
          requestDelegate: {
            onReject: (response) => {
              console.error(response);
              this.errorMessage = response?.message.reasonPhrase || 'Unknown error';
            }
        }});
      }
    } catch (err) {
      this.calling = false;
      this.incomingCall = false;
      this.inCall = false;
      this.errorMessage = err.message;
    }
   }
  private async hangup() {
    try {
      this.errorMessage = '';
      this.calling = false;
      this.incomingCall = false;
      this.inCall = false;
      await this.sipInstance?.hangup();
    } catch (err) {
      this.errorMessage = err.message;
    }
   }

  public render() {
    const buttons = [];
    for (let i = 0; i < this.buttons.length; i++) {
      const option = this.buttons[i].name;
      buttons.push(
        <lar-button
          expand="full"
          color={this.colorInputs}
          disabled={this.disabled}
          onClick={() => this.output.emit(i)}
        >
          <lar-translate t={option} />
        </lar-button>
      );
    }
    return (
      <div class="content">
        {this.camera && <div class="camera">
          {this.cameraLoading && <lar-icon size="large" color={this.colorInputs} icon="loading" />}
        </div> }
        <audio id="remoteAudio" />
        <div class="info">
          {this.calling && <span class="calling"><lar-translate t="intercom.calling" /></span>}
          {this.errorMessage && <span class="error"><lar-translate t={this.errorMessage} /></span>}
        </div>
        <div class="sipcontrols">
            {!this.inCall && <lar-button
              disabled={this.disabled || !this.sipConnected || this.inCall || this.calling}
              outline
              onClick={() => this.call()}
              color="success"
            >
              <lar-icon icon="telephone" />
            </lar-button>
            }
            {this.inCall && <lar-button
              disabled={this.disabled || !this.sipConnected || !this.inCall}
              outline
              onClick={() => this.hangup()}
              color="danger"
            >
              <lar-icon icon="telephone-hangup" />
            </lar-button>
            }
        </div>
        <div class="radios">{buttons}</div>
      </div>
    );
  }
}